// src/app/services/signal-r.service.ts
import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { ConfigService } from './config.service';
import { AccountService } from './account.service';
import { Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  #hubConnection: signalR.HubConnection;
  #notificationSubject = new Subject<any>();
  #currentLocationId: string;

  public notification$: Observable<any> =
    this.#notificationSubject.asObservable();

  constructor(
    private configService: ConfigService,
    private accountService: AccountService
  ) {}

  public startConnection(locationId?: string): void {
    // Store locationId for reconnection
    if (locationId) {
      this.#currentLocationId = locationId;
    }

    // Build URL with locationId query param for group assignment
    const baseUrl = `${this.configService.config.apiUrl}ws-notifications`;
    const url = this.#currentLocationId
      ? `${baseUrl}?locationId=${this.#currentLocationId}`
      : baseUrl;

    this.#hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        withCredentials: true,
        accessTokenFactory: () => this.accountService.getAuthorizationToken(),
      })
      .withAutomaticReconnect()
      .build();

    this.#hubConnection
      .start()
      .then(() => {
        console.log('[SignalR] Connected successfully to:', url);
        console.log('[SignalR] Connection state:', this.#hubConnection.state);
        console.log('[SignalR] LocationId:', this.#currentLocationId);
      })
      .catch((err) => {
        console.error('[SignalR] Connection error:', err);
      });

    this.#hubConnection.on('ReceiveResponse', (message) => {
      console.log('[SignalR] ReceiveResponse received:', message);
      this.#notificationSubject.next(message);
    });

    this.#hubConnection.onreconnecting((error) => {
      console.log('[SignalR] Reconnecting...', error);
    });

    this.#hubConnection.onreconnected((connectionId) => {
      console.log('[SignalR] Reconnected with connectionId:', connectionId);
    });

    this.#hubConnection.onclose((error) => {
      console.log('[SignalR] Connection closed:', error);
    });
  }

  public notifyBarcodeChange(locationId: string, barcode: string): void {
    void this.#hubConnection.invoke('RequestData', {
      type: 'barcode',
      locationId,
      barcode,
    });
  }

  public isConnected(): boolean {
    return this.#hubConnection?.state === signalR.HubConnectionState.Connected;
  }

  public stopConnection(): void {
    if (this.#hubConnection) {
      void this.#hubConnection.stop();
    }
  }
}
